import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle } from "components/ui/dialog";

interface FullScreenDialogProps {
  open: boolean;
  children: React.ReactNode;
  className?: string;
  /**
   * Called when the dialog is dismissed via Escape or an outside interaction.
   * Defaults to navigating back, matching the route-driven booking flows.
   */
  onClose?: () => void;
  /** Accessible name announced by screen readers (WCAG 4.1.2). */
  ariaLabel?: string;
}

const FullScreenDialog = ({
  open,
  children,
  className,
  onClose,
  ariaLabel,
}: FullScreenDialogProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const label = ariaLabel ?? t("common.dialog.label");

  const handleOpenChange = (next: boolean) => {
    if (next) return;
    if (onClose) onClose();
    else navigate(-1);
  };

  return (
    // Modal (Radix default): focus is trapped inside, the rest of the page is
    // aria-hidden from assistive technology, and body scroll is locked — WCAG
    // 2.1.2 / 2.4.3. This was deferred until the nested MUI modals were
    // migrated to Radix (#1299 phases 1-3); their competing focus traps used to
    // fight this one and freeze the page.
    //
    // Note for verification: Radix conveys modality by aria-hiding the
    // background, not via aria-modal="true" — check for the former.
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        position="fullScreen"
        showCloseButton={false}
        // No dimming layer: the panel already covers the viewport on mobile and
        // the flows were designed without a backdrop. Modality does not depend
        // on it — Radix blocks interaction and hides the background regardless.
        withOverlay={false}
        aria-describedby={undefined}
        // Preserve the original behaviour: Escape closes, but an outside click does not.
        onInteractOutside={(event) => event.preventDefault()}
        className={className}
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
};

export default FullScreenDialog;
